import { useEffect, useMemo, useRef, useState } from 'react';

import PointsStore from '@/stores/PointsStore';
import ProfileStore from '@/stores/ProfileStore';
import ResultsStore from '@/stores/ResultsStore';

const DEFAULT_ANON_CURRENCY = 'USD';
const BUSY_APPLYING_POINTS_MS = 1000;

/** `DealComponent`'s constructor / `_onStoreChange` points state, read once for the whole RM3 section. */
function readPointsState() {
    const points = PointsStore.getPoints();
    const preference = ResultsStore.getShowPricesInPoints();
    const profileForPoints = ProfileStore.getProfile();
    const isAnon = Boolean(profileForPoints && profileForPoints.anonymous && !profileForPoints.isLoggedIn);
    const anonPointsExRate = PointsStore.getAnonPointsExRate(
        profileForPoints?.display_currency || DEFAULT_ANON_CURRENCY,
    );
    return {
        points,
        showPricesWithPoints: preference && points.show && points.balance > 0,
        anonPointsOnly: preference && isAnon && anonPointsExRate > 0,
        anonPointsExRate,
    };
}

/**
 * Store-backed price state shared by every RM3 deal row: the points view (`PointsStore` + `ResultsStore`
 * listeners, as `DealComponent`) and the one-second "applying points" placeholder shown after
 * `arbitripPointsApplied` flips (`DealComponent.componentDidUpdate`).
 */
export default function useRm3PriceState(arbitripPointsApplied) {
    const [pointsState, setPointsState] = useState(readPointsState);
    const [busyApplyingPoints, setBusyApplyingPoints] = useState(false);
    const previousApplied = useRef(arbitripPointsApplied);

    useEffect(() => {
        const onStoreChange = () => setPointsState(readPointsState());
        PointsStore.addChangeListener(onStoreChange);
        ResultsStore.addChangeListener(onStoreChange);
        return () => {
            PointsStore.removeChangeListener(onStoreChange);
            ResultsStore.removeChangeListener(onStoreChange);
        };
    }, []);

    useEffect(() => {
        if (previousApplied.current === arbitripPointsApplied) {
            return undefined;
        }
        previousApplied.current = arbitripPointsApplied;
        setBusyApplyingPoints(true);
        const timeout = setTimeout(() => setBusyApplyingPoints(false), BUSY_APPLYING_POINTS_MS);
        return () => clearTimeout(timeout);
    }, [arbitripPointsApplied]);

    return useMemo(() => ({ ...pointsState, busyApplyingPoints }), [pointsState, busyApplyingPoints]);
}
